import { Injector } from '@angular/core';
import { firstValueFrom, merge, Observable, Subject, Subscription } from 'rxjs';
import {
    Chips,
    RequestToSitPayload,
    RequestTopUpPayload,
    RequestToStandPayload,
    ClientSocketEvent,
    RequestToSitOut,
    RequestToDeleteTable,
    ClubRole,
    UpdateVisitorPayload,
    TableHostSocketType,
    SetIpAddressPayload,
    IClubState,
} from '@soposopo/sopo-models';
import { AnswerMultipleTimesPayload, SetMultipleTimesPayload } from '@soposopo/sopo-models/dist/socketPayload/TableHostPayload';
import { GameServer } from './base/game-server.class';
import { ClubStateDispatcher } from '../../state/club/club.dispatcher';
import { GameStateId } from '../../state/game/game.state';
import { ClubService } from '../../firebase/club.service';
import { DEFAULT_DISMISS_AFTER_TIME } from 'src/app/sopo-popup/service/sopo-popup.service';

export class RingGameServer extends GameServer {
    //SERVICES
    private clubDispatcher: ClubStateDispatcher;
    private clubService: ClubService;

    private tableMessageSubscription: Subscription;
    private constructSubscription: Subscription;
    clubSubscription: Subscription;

    private topUpResponse = new Subject<any>();

    constructor(
        protected url: string, protected gameStateId: GameStateId, protected injector: Injector
    ) {
        super();
        this.init();
    }

    protected injectServices() {
        super.injectServices();
        this.clubDispatcher = this.injector.get(ClubStateDispatcher);
        this.clubService = this.injector.get(ClubService);
    }
    
    async steppedAwayAlert(data: any) {
        if (data.type === 'stepped_away_player') {
            if (this.clientID === data.playerID) {
                this.sopoPopupService.showAlert(
                    'game',
                    {
                        header: 'Oops !!!',
                        message: data.message,
                        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                    },
                );
            }
        }
    }

    async showTableJoinError(data: ITableJoinErrorSocketData) {
        if (this.alertPresented || this.clientID !== data.playerID) return;
        let header = ``;
        if (data.type === 'player_sit') header = `This seat is already taken`;
        if (data.type === 'player_sit_same_hand') header = `Cannot sit during the same hand`;
        if (data.type === 'player_distance') header = `Cannot sit at the table`;
        if (header.length <= 0) return;
        this.alertPresented = true;
        const alertRef = this.sopoPopupService.showAlert(
            'game',
            {
                header,
                message: data.message,
                dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            },
        );
        if (alertRef) await firstValueFrom(alertRef.instance.getAlertClosed());
        this.alertPresented = false;
    }

    /**
     * Subscribe to various socket events [onConnect, onDisconnect, tableMessage, dealerReset, dealerMessage]
     */
    protected listenToMessages() {
        super.listenToMessages();
        this.tableMessageSubscription = merge(this.getTableMessage(), super.getDealerError()).subscribe((data) => {
            switch(data.socketType) {
                case TableHostSocketType.PlayerDistance:
                    this.showDistanceAlert(data);
                    break;
                case TableHostSocketType.MatchingIPAddress:
                    this.showMatchingIPAlert(data);
                    break;
                case TableHostSocketType.TopUpSuccess:
                    this.showTopUpMessage(data);
                    break;
                case TableHostSocketType.StepAwayFailed:
                case TableHostSocketType.NotEnoughChips:
                case TableHostSocketType.BuyinOutOfLimits:
                    this.showInGameMessage(data);
                    break;
                case TableHostSocketType.TableDelete:
                    this.showDeleteTableAlert(data);
                    break;
                case TableHostSocketType.PlayerExit:
                case TableHostSocketType.OwnerExit:
                case TableHostSocketType.PlayerExitTable:
                case TableHostSocketType.ExitPlayerNotInPlay:
                    this.dismissPlayersFromTable(data);
                    break;
                case TableHostSocketType.UnionOwnerExit:
                case TableHostSocketType.UnionPlayerExit:
                case TableHostSocketType.UnionSitAloneExit:
                case TableHostSocketType.UnionNotSeatedPlayer:
                    this.dismissUnionPlayersFromTable(data);
                    break;
                case TableHostSocketType.TableInitialized:
                    this.addToVisitor(data); //TODO
                    break;     
                case TableHostSocketType.TopupRejected:
                    this.topUpRejected(data);
                    break;                     
                case TableHostSocketType.ClubDataDoesNotExist:    
                case TableHostSocketType.UndefinedTableHost: 
                case TableHostSocketType.InvalidPatronID: 
                    this.showMessage(data);
                    break;  
            }
            this.gameSocketsDispatcher.acceptTableSignal(data);
        });

        this.constructSubscription = this.getSteppedAwayRemove().subscribe((data) => {
            this.steppedAwayAlert(data);
        });

        const errorSubscription = this.getTableJoinError().subscribe((message) => {
            this.showTableJoinError(message);
        });
        this.constructSubscription.add(errorSubscription);
    }

    private async addToVisitor(data) {
        const userID = await this.profileService.getMyID();
        if (data.playerID === userID) {
            this.updateVisitorList(data.tableID, data.playerID);
        }
    }

    private async topUpRejected(data) {
        const userID = await this.profileService.getMyID();
        if (data.playerID === userID) {
            if (data.topUp > 0) {
                this.sopoPopupService.showAlert(
                    'game',
                    {
                        header: 'Topup Rejected',
                        message: data.message,
                    },
                );
            }
        }
    }

    async navigateToClubLobby(data, gameStateId: GameStateId) {
        const gameState = await firstValueFrom(this.gameDispatcher.getState());
        const clubID = gameState.games[gameStateId].table.clubID;
        if (this.clientID === data.playerID) {
            this.navCtrl.navigateBack(`club-lobby/${clubID}`);
        }
    }

    private async dismissPlayersFromTable(data: any) {
        const clientRole = await this.clubService.getMyRole();
        if (clientRole === ClubRole.OWNER && this.clientID === data.playerID) {
            this.dimissOwner(data);
        } else if (this.clientID === data.playerID) {
            this.dismissPlayers(data);
        }

        if (!data.playerID) {
            if (clientRole === ClubRole.OWNER) {
                this.dimissOwner(data);
            } else {
                this.dismissPlayers(data);
            }
        }
    }

    private async dismissUnionPlayersFromTable(data) {
        if (this.clientID === data.playerID && data.type === 'union_owner_exit') {
            this.dimissOwner(data);
        } else if (
            this.clientID === data.playerID &&
            data.type != 'union_sit_alone_exit' &&
            data.type != 'union_not_seated_player'
        ) {
            this.dismissPlayers(data);
        }

        if (data.type == 'union_sit_alone_exit' || data.type == 'union_not_seated_player') {
            let tableOwner;
            this.clubSubscription = this.clubDispatcher.getClubData().subscribe((clubState: IClubState) => {
                tableOwner = Object.keys(clubState.games).includes(data.tableID);
            });
            if (tableOwner) {
                this.dimissOwner(data);
            } else {
                this.dismissPlayers(data);
            }
        }
    }

    private async dimissOwner(data: any) {
        // const gameStateId = await this.gameDispatcher.getGameStateId(data.tableID);
        // if (gameStateId != null) {
        //     this.gameDispatcher.removeGame(gameStateId);
        // }
    }

    private async dismissPlayers(data: any) {
        const alertHeader = 'Table Deleted';
        if (this.clientID === data.playerID) {
            this.sopoPopupService.showAlert(
                'game',
                {
                    header: alertHeader,
                    message: data.message,
                    dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                },
            );        
        }   
    }

    async showInGameMessage(data) {
        if (this.clientID === data.playerID) {
            const gameStateId = await this.gameDispatcher.getGameStateId(data.tableID);
            if (gameStateId != null) {
                this.sopoPopupService.showAlert(
                    'game-container',
                    {
                        header: data.header,
                        message: data.message,
                        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                    },
                    gameStateId,
                );
            }
        }
    }

    private async showTopUpMessage(data) {
        if (this.clientID === data.playerID) {
            this.topUpResponse.next(data.type);
            const gameStateId = await this.gameDispatcher.getGameStateId(data.tableID);
            if (gameStateId != null) {
                this.sopoPopupService.showAlert(
                    'game-container',
                    {
                        header: data.header,
                        message: data.message,
                        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                    },
                    gameStateId,
                );
            }
        }
    }

    async showDistanceAlert(data) {
        if (this.clientID === data.playerID) {
            const gameStateId = await this.gameDispatcher.getGameStateId(data.tableID);
            if (gameStateId != null) {
                this.sopoPopupService.showAlert(
                    'game-container',
                    {
                        header: data.header,
                        message: ' You are to close proximity to another player.',
                        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                    },
                    gameStateId,
                );
            }
        }
    }

    async showMatchingIPAlert(data) {
        if (this.clientID === data.playerID) {
            const gameStateId = await this.gameDispatcher.getGameStateId(data.tableID);
            if (gameStateId != null) {
                this.sopoPopupService.showAlert(
                    'game-container',
                    {
                        header: data.header,
                        message: 'You are in the same network with another player.',
                        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                    },
                    gameStateId,
                );
            }
        }
    }

    private async joinRequestMessagePopup(data) { 
        this.loadingCtrl.dismiss('restricted'); 
        const gameStateId = await this.gameDispatcher.getGameStateId(data.tableID);
        if (gameStateId != null) {
            this.sopoPopupService.showAlert(
                'game-container',
                {
                    header: data.header,
                    message: data.message,
                    dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                },
                gameStateId,
            );
        }
        else {
            this.sopoPopupService.showAlert(
                'game',
                {
                    header: data.header,
                    message: data.message,
                    dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                },
            );
        }
    }


    private async showDeleteTableAlert(data) {
        const clientRole = await this.clubService.getMyRole();
        if (clientRole === ClubRole.OWNER) {
            this.sopoPopupService.showAlert(
                'game',
                {
                    header: 'Table Delete Alert',
                    message: data.message,
                    dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                },
            );
        }
    }

    async showMessage(data) {
        if (this.clientID === data.playerID) {
            this.sopoPopupService.showAlert(
                'game',
                {
                    header: data.header,
                    message: data.message,
                    dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                },
            );
        }
    }

    private async updateVisitorList(tableID: string, userID: string): Promise<void> {
        const payload: UpdateVisitorPayload = { tableID, userID };
        this.socket.emit(ClientSocketEvent.UPDATE_VISITOR, payload);
        return Promise.resolve();
    }

    async requestToSit(
        tableID: string,
        userID: string,
        stack: Chips,
        seatNumber: number,
        nextMaxBuyIn: Chips,
        latitude: number,
        longitude: number,
        clubId: string,
    ): Promise<void> {
        const payload: RequestToSitPayload = {
            tableID,
            userID,
            stack,
            seatNumber,
            nextMaxBuyIn,
            latitude,
            longitude,
            clubId
        };
        this.socket.emit(ClientSocketEvent.SIT, payload);
        return Promise.resolve();
    }

    async requestTopUp(tableID: string, userID: string, topUp: number, nextMaxBuyIn: Chips) {
        const payload: RequestTopUpPayload = { tableID, userID, topUp, nextMaxBuyIn };
        this.socket.emit(ClientSocketEvent.TOP_UP, payload, () => Promise.resolve());
    }

    async requestWaitBB(tableID: string, userID: string) {
        const payload: RequestToSitOut = { tableID, userID };
        this.socket.emit(ClientSocketEvent.COME_BACK_ON_BLIND, payload, () => Promise.resolve());
    }

    async requestSitOutBlind(tableID: string, userID: string) {
        const payload: RequestToSitOut = { tableID, userID };
        this.socket.emit(ClientSocketEvent.SIT_OUT_NEXT_BLIND, payload, () => Promise.resolve());
    }

    async requestToStand(tableID: string, userID: string) {
        const payload: RequestToStandPayload = { tableID, userID };
        this.socket.emit(ClientSocketEvent.STAND_UP, payload);
    }

    async resetDeleteTableState(tableID: string) {
        const payload: RequestToDeleteTable = { tableID, forceDelete: false };
        this.socket.emit(ClientSocketEvent.RESET_DELETE_TABLE, payload);
    }

    setIp(tableID: string, playerIDTemp: string){
        const playerId = playerIDTemp === undefined ? this.clientID : playerIDTemp;
        const payload: SetIpAddressPayload  = { tableId: tableID, playerId, ip:'' };
        this.socket.emit(ClientSocketEvent.SET_IP_ADDRESS,payload);
    }

    answerRunMultipleTimes(tableID: string, userID: string, answer: boolean) {
        const payload: AnswerMultipleTimesPayload = { tableID, userID, answer};
        this.socket.emit(ClientSocketEvent.ANSWER_RUN_MULTIPLE_TIMES, payload);
    }

    setRunMultipleTimes(tableID: string, userID: string, option: number) {
        const payload: SetMultipleTimesPayload = { tableID, userID, option};
        this.socket.emit(ClientSocketEvent.SET_RUN_MULTIPLE_TIMES, payload);
    }

    private getSteppedAwayRemove(): Observable<any> {
        return this.socket.fromEvent('steppedAwayRemove');
    }

    private getTableJoinError(): Observable<any> {
        return this.socket.fromEvent('tableJoinError');
    }

    getTopUpResponse(): Observable<any> {
        return this.topUpResponse.asObservable();
    }

    protected stopListeningToMessages() {
        super.stopListeningToMessages();
        if (this.tableMessageSubscription) {
            this.tableMessageSubscription.unsubscribe();
        }
        if (this.constructSubscription) {
            this.constructSubscription.unsubscribe();
        }
        if (this.clubSubscription) {
            this.clubSubscription.unsubscribe();
        }
    }
}

interface ITableJoinErrorSocketData {
    type: 'player_sit' | 'player_sit_same_hand' | 'player_distance';
    playerID: string;
    message: string;
}
